// Files an agent hands back by path. An agent that takes a screenshot or
// generates an image saves it — usually under the worktree's `.context` — and
// names it in its answer as inline code, a link or a Markdown image. Those
// paths are drawn as attachments under the message, the way files a user sends
// are, and open in the workspace's preview. Mirrored by `MessageAttachments` in
// the Swift client.

import { isPreviewableImagePath } from "@rivets/core";
import { parseFileLink, type FileLinkTarget } from "./fileLinks";
import type { BlockNode, InlineNode } from "./markdown";

export interface MessageAttachment {
  /** The path as written; resolved against the worktree when opened. */
  path: string;
  /** The file's own name, which is what the attachment shows. */
  name: string;
}

/** Enough for a run of screenshots without an answer becoming a gallery. */
export const MESSAGE_ATTACHMENT_LIMIT = 8;

/**
 * Reads a code span or a link destination as an image the agent produced, or
 * null. Only images count: agents cite source files in code spans constantly,
 * and those stay code. A glob or a home-relative path names nothing that can be
 * opened.
 */
export function messageAttachmentTarget(value: string): FileLinkTarget | null {
  const trimmed = value.trim();
  if (!trimmed || trimmed.includes("\n") || /[*?{}[\]<>|]/.test(trimmed) || trimmed.startsWith("~")) return null;
  const target = parseFileLink(trimmed);
  if (!target || !isPreviewableImagePath(target.path)) return null;
  return { path: target.path, line: null, endLine: null };
}

/** Image files a message names, in reading order, once each. Code blocks are
 * left out: a path in a script is not something handed back. */
export function extractMessageAttachments(blocks: readonly BlockNode[]): MessageAttachment[] {
  const found: MessageAttachment[] = [];
  const seen = new Set<string>();
  const add = (value: string) => {
    if (found.length >= MESSAGE_ATTACHMENT_LIMIT) return;
    const target = messageAttachmentTarget(value);
    if (!target || seen.has(target.path)) return;
    seen.add(target.path);
    found.push({ path: target.path, name: target.path.slice(target.path.lastIndexOf("/") + 1) });
  };
  const inline = (nodes: readonly InlineNode[]) => {
    for (const node of nodes) {
      if (node.type === "code") add(node.value);
      else if (node.type === "link") add(node.href);
      else if (node.type === "strong" || node.type === "em" || node.type === "strike") inline(node.children);
    }
  };
  const walk = (nodes: readonly BlockNode[]) => {
    for (const block of nodes) {
      switch (block.type) {
        case "paragraph":
        case "heading":
          inline(block.children);
          break;
        case "quote":
          walk(block.children);
          break;
        case "list":
          block.items.forEach(walk);
          break;
        case "table":
          block.header.forEach(inline);
          block.rows.forEach((row) => row.forEach(inline));
          break;
        case "code":
        case "rule":
          break;
      }
    }
  };
  walk(blocks);
  return found;
}

/** A cheap test that spares parsing a message that names no image at all. */
export function mayNameImage(text: string): boolean {
  return /\.(?:png|jpe?g|gif|webp|bmp|ico|avif)\b/i.test(text);
}
